import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Video, Phone, Lock, Eye, EyeOff, Users } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GDOLoginPage = () => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/gdo/login`, { phone, password });
      // Hand the token + user payload to AuthContext so SPA navigation works
      // without a hard refresh (fixes bubble-unassigned redirect-to-login).
      adoptSession?.(r.data.token, r.data.user);
      localStorage.setItem("ysv_gdo", "true");
      navigate("/gdo/portal");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid phone number or password");
    }
    setLoading(false);
  };

  return (
    <div data-testid="gdo-login-page" style={{
      minHeight: '100vh', background: '#0a0a0c', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 24,
    }}>
      <div style={{ maxWidth: 400, width: '100%' }}>
        {/* Back Arrow */}
        <Link to="/landing" data-testid="gdo-back-btn" style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 44, height: 44, borderRadius: '50%', background: '#1e2732',
          textDecoration: 'none', color: '#fff', marginBottom: 32,
        }}><ArrowLeft size={20} /></Link>

        {/* Camera Icon */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, #4a2e1e, #3d2517)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <Video size={32} style={{ color: '#ff6b00' }} />
          </div>
          <h1 data-testid="gdo-title" style={{
            fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 800,
            color: '#ff6b00', marginBottom: 4,
          }}>Gameday Operator</h1>
          <p style={{ color: '#999', fontSize: 15 }}>YSV Staff Portal</p>
        </div>

        {/* Login Form */}
        <div style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Operator Login</h2>
          <p style={{ color: '#999', fontSize: 14, marginBottom: 24 }}>Sign in with your phone number</p>

          <form data-testid="gdo-login-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error && <div data-testid="gdo-login-error" style={{ background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 10, padding: '10px 14px', color: '#f44336', fontSize: 13 }}>{error}</div>}

            {/* Phone Input */}
            <div style={{ position: 'relative' }}>
              <Phone size={18} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
              <input data-testid="gdo-phone-input" type="tel" placeholder="(555) 123-4567"
                value={phone} onChange={e => setPhone(e.target.value)} required
                style={{
                  width: '100%', padding: '16px 16px 16px 48px', background: '#1e2732',
                  border: '1px solid #2a3a4a', borderRadius: 12, color: '#fff', fontSize: 16,
                  outline: 'none', fontFamily: "'DM Sans', sans-serif",
                }}
              />
            </div>

            {/* Password Input */}
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
              <input data-testid="gdo-password-input" type={showPw ? 'text' : 'password'} placeholder="Password"
                value={password} onChange={e => setPassword(e.target.value)} required
                style={{
                  width: '100%', padding: '16px 48px 16px 48px', background: '#1e2732',
                  border: '1px solid #2a3a4a', borderRadius: 12, color: '#fff', fontSize: 16,
                  outline: 'none', fontFamily: "'DM Sans', sans-serif",
                }}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: '#888', cursor: 'pointer',
              }}>{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>

            {/* Sign In Button */}
            <button type="submit" data-testid="gdo-signin-btn" disabled={loading} style={{
              width: '100%', padding: '16px', background: '#ff6b00', border: 'none',
              borderRadius: 12, color: '#fff', fontSize: 17, fontWeight: 700,
              cursor: 'pointer', marginTop: 8, opacity: loading ? 0.7 : 1,
              fontFamily: "'Outfit', sans-serif",
              transition: 'opacity 0.2s, transform 0.1s',
            }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Help text */}
          <p style={{ textAlign: 'center', color: '#777', fontSize: 13, marginTop: 24 }}>
            Need help? Contact your coordinator
          </p>

          {/* Switch to Team/Player Login */}
          <Link to="/login" data-testid="gdo-switch-login" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            textDecoration: 'none', marginTop: 20, color: 'var(--cyan)', fontSize: 15, fontWeight: 600,
          }}>
            <Users size={18} style={{ color: '#ff6b00' }} />
            Switch to Team/Player Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GDOLoginPage;
